import store from 'store'
import uuid from 'uuid'
import { get } from 'lodash'

import { toDash, getLocaleFromStore } from 'utils/intl'
import { normalizeMessages } from 'models/configurations'

const NOTIFICATION = 'react-template/app/NOTIFICATION'
const CHANGE_LOCALE = 'react-template/app/CHANGE_LOCALE'
const TOGGLE_MODAL = 'react-template/app/TOGGLE_MODAL'
const UPDATE_ACCORDION = 'react-template/app/UPDATE_ACCORDION'
const UPDATE_TAB = 'react-template/app/UPDATE_TAB'
const SET_PAGE = 'react-template/app/SET_PAGE'
const REFRESH_PAGE = 'react-template/app/REFRESH_PAGE'
const SET_COMMON_LIMIT = 'react-template/app/SET_COMMON_LIMIT'
const NO_CONNECTION = 'react-template/app/NO_CONNECTION'
const NO_API_CONNECTION = 'react-template/app/NO_API_CONNECTION'
const GLOBAL_LOADING = 'react-template/app/GLOBAL_LOADING'
const REQUEST_DOWNLOAD = 'react-template/app/REQUEST_DOWNLOAD'
const ON_ERROR = 'react-template/app/ON_ERROR'
const GET_LOCALIZED_MESSAGES = 'react-template/app/GET_LOCALIZED_MESSAGES'
const GET_LOCALIZED_MESSAGES_SUCCESS =
  'react-template/app/GET_LOCALIZED_MESSAGES_SUCCESS'
const GET_LOCALIZED_MESSAGES_FAIL =
  'react-template/app/GET_LOCALIZED_MESSAGES_FAIL'
const SAVE_COOKIE_LOCALE = 'react-template/app/SAVE_COOKIE_LOCALE'

export const DOFE_RESET_STATE = 'react-template/app/DOFE_RESET_STATE'

const initialState = {
  notification: {
    message: '',
    level: '',
    options: {},
    active: false
  },
  locale: getLocaleFromStore() || {
    cookieLocaleCode: undefined,
    id: 2,
    // short: 'en',
    name: 'English',
    code: 'en-gb',
    willUpdate: false
  },
  messagesFetchComplete: false,
  messagesFetchedLocaleCode: null,
  messages: {},
  page: {
    title: '',
    breadcrumbs: false
  },
  modal: {
    id: null,
    next: null,
    data: null
  },
  download: {
    requestedSrc: ''
  },
  misc: {
    updateAccordion: null,
    refreshPage: false,
    noConnection: false,
    noApiConnection: false,
    loading: false
  },
  tabs: {
    participantLevels: 0
  },
  commonLimit: 20,
  error: {}
}

export default function reducer(state = initialState, action = {}) {
  const code = action.code || 'en-gb'

  switch (action.type) {
    case NOTIFICATION:
      return Object.assign({}, state, {
        notification: {
          message: action.message,
          level: typeof action.options === 'string' ? action.options : 'info',
          options: typeof action.options === 'object' ? action.options : {},
          active: action.active === undefined
        }
      })
    case CHANGE_LOCALE:
      store.set(
        'locale',
        JSON.stringify({
          ...action.locale,
          code: toDash(action.locale.code)
        })
      )

      // write to cookie for SSR HTML request
      if (!__SERVER__) {
        document.cookie = `cookieLocaleCode=${get(action, 'locale.code')}`
      }

      return {
        ...state,
        locale: {
          ...state.locale,
          id: action.locale.id,
          translationLocale: action.locale.translationLocale,
          name: action.locale.languageName,
          parentLocale: action.locale.parentTranslationLocale,
          parent: action.locale.parent,
          isTranslationLocale: action.locale.isTranslationLocale,
          code: toDash(action.locale.code),
          willUpdate: true
        }
      }
    case TOGGLE_MODAL:
      return Object.assign({}, state, {
        modal: {
          id: action.id,
          next: action.next,
          data: action.data
        }
      })
    case GET_LOCALIZED_MESSAGES:
      return Object.assign({}, state, {
        messagesFetchComplete: false,
        messages: {}
      })
    case GET_LOCALIZED_MESSAGES_SUCCESS:
      return Object.assign({}, state, {
        messagesFetchComplete: true,
        messagesFetchedLocaleCode: get(action, 'code'),
        messages: {
          ...state.messages,
          [toDash(code)]: normalizeMessages(action.result)
        }
      })
    case GET_LOCALIZED_MESSAGES_FAIL:
      // if failed and Messages are null then local Messages will be loaded
      return {
        ...state,
        messagesFetchComplete: true,
        messages: {}
      }
    case SAVE_COOKIE_LOCALE:
      // if failed and Messages are null then local Messages will be loaded
      return Object.assign({}, state, {
        locale: {
          ...state.locale,
          cookieLocaleCode: action.code
        }
      })
    case UPDATE_ACCORDION:
      return Object.assign({}, state, {
        misc: {
          ...state.misc,
          updateAccordion: uuid.v1()
        }
      })
    case UPDATE_TAB:
      return Object.assign({}, state, {
        ...state,
        tabs: {
          ...state.tabs,
          [action.tabKey]: action.id
        }
      })
    case REQUEST_DOWNLOAD:
      return Object.assign({}, state, {
        ...state,
        download: {
          ...state.download,
          requestId: action.requestId,
          requestedSrc: action.requestedSrc
        }
      })
    case SET_PAGE:
      return {
        ...state,
        page: {
          title: action.title,
          breadcrumbs: action.breadcrumbs,
          options: action.options
        }
      }
    case REFRESH_PAGE:
      return {
        ...state,
        misc: {
          ...state.misc,
          refreshPage: true
        }
      }
    case NO_CONNECTION:
      return {
        ...state,
        misc: {
          ...state.misc,
          noConnection: action.bool
        }
      }
    case NO_API_CONNECTION:
      return {
        ...state,
        misc: {
          ...state.misc,
          noApiConnection: action.bool
        }
      }
    case GLOBAL_LOADING:
      return {
        ...state,
        misc: {
          ...state.misc,
          loading: action.bool
        }
      }
    case SET_COMMON_LIMIT:
      return {
        ...state,
        commonLimit: action.commonLimit
      }
    case ON_ERROR:
      return {
        ...state,
        error: {
          ...action.error,
          options: action.options
        }
      }
    default:
      return state
  }
}

export function notification(message, options, active) {
  return {
    type: NOTIFICATION,
    message,
    options,
    active
  }
}

export function getLocalizedMessages({ code } = {}) {
  return {
    types: [
      GET_LOCALIZED_MESSAGES,
      GET_LOCALIZED_MESSAGES_SUCCESS,
      GET_LOCALIZED_MESSAGES_FAIL
    ],
    promise: client => client.get(`api/poe-translations/bo/${code}`),
    code
  }
}

export function saveCookieLocale({ code } = {}) {
  return {
    type: SAVE_COOKIE_LOCALE,
    code
  }
}

export function changeLocale(locale = {}) {
  return {
    type: CHANGE_LOCALE,
    locale
  }
}

export function modal(id = null, next, data = null) {
  return {
    type: TOGGLE_MODAL,
    id,
    next,
    data
  }
}

export function resetState() {
  return {
    type: DOFE_RESET_STATE
  }
}

export function requestDownload({ requestId, requestedSrc } = {}) {
  return {
    type: REQUEST_DOWNLOAD,
    requestId,
    requestedSrc
  }
}

export function updateAccordion() {
  return {
    type: UPDATE_ACCORDION
  }
}

export function updateActiveTab(tabKey, id) {
  return {
    type: UPDATE_TAB,
    tabKey,
    id
  }
}

export function setPage(title, breadcrumbs, options) {
  return {
    type: SET_PAGE,
    title,
    breadcrumbs,
    options
  }
}

export function onRefreshPage(bool) {
  return {
    type: REFRESH_PAGE,
    bool
  }
}

export function noActiveConnection(bool) {
  return {
    type: NO_CONNECTION,
    bool
  }
}

export function noApiConnection(bool) {
  return {
    type: NO_API_CONNECTION,
    bool
  }
}

export function setGlobalLoading(bool) {
  return {
    type: GLOBAL_LOADING,
    bool
  }
}

export function setCommonLimit(commonLimit) {
  return {
    type: SET_COMMON_LIMIT,
    commonLimit
  }
}

export function logError(error, options) {
  return {
    type: ON_ERROR,
    error,
    options
  }
}
